import {
  FaAndroid,
  FaApple,
  FaBrain,
  FaBullhorn,
  FaChartLine,
  FaCloud,
  FaCloudDownloadAlt,
  FaCogs,
  FaCommentsDollar,
  FaDatabase,
  FaDesktop,
  FaEnvelopeOpenText,
  FaExchangeAlt,
  FaGlobe,
  FaLaptopCode,
  FaMobileAlt,
  FaNetworkWired,
  FaPalette,
  FaPenNib,
  FaPlug,
  FaReact,
  FaRobot,
  FaSearch,
  FaServer,
  FaShieldAlt,
  FaSitemap,
  FaSyncAlt,
  FaTools,
  FaUserTie,
  FaUsers,
  FaVideo,
} from "react-icons/fa";

export const servicePageContent = {
  buildWebsites: {
    eyebrow: "Websites",
    title: "Launch a website that is easy to trust, read, and act on.",
    description:
      "We design and build responsive websites that make your offer clear, load quickly, and guide visitors toward the next step.",
    cta: "Get a Website Quote",
    color: "#FF6F20",
    icon: FaLaptopCode,
    timeline: "Typical scope: 2-6 weeks",
    bestFor: "Brands, founders, and teams preparing a public launch",
    featuresIntro:
      "Your website should help people understand what you do without making them work for it. We focus on structure, polish, performance, and conversion.",
    features: [
      {
        icon: FaLaptopCode,
        title: "Custom Pages",
        text: "Purpose-built pages for your brand, services, products, portfolio, or campaign.",
      },
      {
        icon: FaMobileAlt,
        title: "Responsive Experience",
        text: "Layouts and content tuned for phone, tablet, laptop, and desktop visitors.",
      },
      {
        icon: FaSearch,
        title: "SEO Foundation",
        text: "Clean page structure, metadata, fast-loading assets, and readable content hierarchy.",
      },
      {
        icon: FaChartLine,
        title: "Conversion Flow",
        text: "Clear CTAs, trust signals, and page sections arranged around user intent.",
      },
    ],
    snapshot: [
      { label: "Primary goal", value: "Turn visitors into qualified leads or customers." },
      { label: "What we need", value: "Brand assets, service details, references, and launch goals." },
      { label: "Common deliverable", value: "A responsive marketing website with contact flow." },
    ],
    outcomes: [
      "A polished web presence that explains your offer quickly.",
      "Reusable sections that keep future content updates easier.",
      "Fast, mobile-friendly pages with fewer friction points.",
      "A clear path from first visit to contact or purchase intent.",
    ],
    included: [
      "Content structure and page planning.",
      "UI design and responsive implementation.",
      "Contact, quote, or enquiry flow setup.",
      "Launch checks for responsiveness, links, and basic SEO.",
    ],
    processIntro:
      "We move from clarity to design to build, keeping each stage visible so the launch does not become a guessing game.",
    process: [
      { title: "Map the Site", text: "Define audience, pages, content needs, and conversion goals." },
      { title: "Design the Flow", text: "Create layouts that make the message easy to scan and trust." },
      { title: "Build and Connect", text: "Implement responsive pages, forms, analytics hooks, and core integrations." },
      { title: "Review and Launch", text: "Test across devices, polish content, and prepare the site for handoff." },
    ],
    related: ["Ad Creatives", "Web Applications", "Consultation Services"],
  },

  mobileApps: {
    eyebrow: "Mobile Apps",
    title: "Build mobile apps that feel simple, fast, and useful from day one.",
    description:
      "We create Android and iOS experiences around real user workflows, with careful attention to performance, onboarding, and release readiness.",
    cta: "Plan My App",
    color: "#008BFF",
    icon: FaMobileAlt,
    timeline: "Typical scope: 6-14 weeks",
    bestFor: "Products that need daily engagement or field access",
    featuresIntro:
      "A good mobile app is more than screens. It needs a focused product flow, practical offline behavior, notifications where useful, and a launch path.",
    features: [
      {
        icon: FaApple,
        title: "iOS Experience",
        text: "Apple-friendly interaction patterns, clean screens, and App Store preparation.",
      },
      {
        icon: FaAndroid,
        title: "Android Experience",
        text: "Android-ready flows, device coverage, and Play Store release support.",
      },
      {
        icon: FaShieldAlt,
        title: "Secure Accounts",
        text: "Authentication, role-aware flows, and careful handling of user data.",
      },
      {
        icon: FaCloud,
        title: "Backend Connected",
        text: "APIs, databases, storage, notifications, and admin tools connected cleanly.",
      },
    ],
    snapshot: [
      { label: "Primary goal", value: "Turn product workflows into a smooth mobile experience." },
      { label: "What we need", value: "Core use cases, user roles, feature priority, and platform targets." },
      { label: "Common deliverable", value: "A release-ready mobile MVP with backend support." },
    ],
    outcomes: [
      "A focused app flow users can understand without training.",
      "Reliable API-backed data and account management.",
      "Responsive screens that work across common device sizes.",
      "A launch plan for store submission and early iteration.",
    ],
    included: [
      "Product flow mapping and screen planning.",
      "UI design for core user journeys.",
      "App development with testing on target platforms.",
      "Release guidance and post-launch improvement notes.",
    ],
    processIntro:
      "We start with the smallest useful version, then build around the behaviors that make the app valuable.",
    process: [
      { title: "Define Journeys", text: "Clarify users, jobs to be done, and the core app flow." },
      { title: "Prototype Screens", text: "Design the experience before committing to build complexity." },
      { title: "Develop and Test", text: "Implement app screens, APIs, data handling, and device checks." },
      { title: "Launch and Learn", text: "Prepare release assets and plan the first improvement cycle." },
    ],
    related: ["API Gateways", "Web Applications", "AI Agents"],
  },

  webApplications: {
    eyebrow: "Web Apps",
    title: "Create web applications that organize real work, not just screens.",
    description:
      "We build secure, scalable web apps for dashboards, SaaS products, internal tools, portals, and customer-facing workflows.",
    cta: "Scope My Web App",
    color: "#FFB400",
    icon: FaGlobe,
    timeline: "Typical scope: 6-16 weeks",
    bestFor: "SaaS platforms, dashboards, portals, and business tools",
    featuresIntro:
      "Complex products become easier when the data model, roles, workflows, and interface are designed together.",
    features: [
      {
        icon: FaReact,
        title: "Modern Frontend",
        text: "Interactive interfaces with responsive layouts and reusable components.",
      },
      {
        icon: FaDatabase,
        title: "Backend Systems",
        text: "APIs, databases, queues, file handling, and admin workflows built to fit the product.",
      },
      {
        icon: FaShieldAlt,
        title: "Access Control",
        text: "Authentication, authorization, validation, and data protection from the start.",
      },
      {
        icon: FaNetworkWired,
        title: "Scalable Structure",
        text: "Architecture choices that leave room for growth, integrations, and new modules.",
      },
    ],
    snapshot: [
      { label: "Primary goal", value: "Transform operational logic into a clear product workflow." },
      { label: "What we need", value: "User roles, data requirements, core actions, and success metrics." },
      { label: "Common deliverable", value: "A production-ready web app with admin and user flows." },
    ],
    outcomes: [
      "Cleaner workflows for teams or customers.",
      "A maintainable codebase with reusable modules.",
      "Secure user access and reliable data handling.",
      "Room to add integrations, analytics, and future features.",
    ],
    included: [
      "Requirements and feature prioritization.",
      "Database and API design.",
      "Frontend and backend implementation.",
      "Testing, deployment support, and handoff documentation.",
    ],
    processIntro:
      "We de-risk the product by mapping workflows first, then building the smallest complete system that proves the core value.",
    process: [
      { title: "Plan the Product", text: "Define roles, workflows, data, permissions, and MVP boundaries." },
      { title: "Design the System", text: "Shape UI flows, architecture, database models, and API contracts." },
      { title: "Build Modules", text: "Develop the app in usable slices with review points along the way." },
      { title: "Deploy and Improve", text: "Launch, monitor, and identify the next set of practical improvements." },
    ],
    related: ["API Gateways", "CRM Platforms", "AI Agents"],
  },

  apiGateways: {
    eyebrow: "API Gateways",
    title: "Connect your systems with APIs that are secure, documented, and reliable.",
    description:
      "We design gateway layers, integration flows, and API contracts that help frontend apps, services, and third-party platforms work together.",
    cta: "Discuss Integration",
    color: "#00C2CB",
    icon: FaNetworkWired,
    timeline: "Typical scope: 2-8 weeks",
    bestFor: "Teams connecting apps, services, vendors, or internal tools",
    featuresIntro:
      "Integrations become painful when auth, rate limits, errors, and ownership are unclear. We make those details explicit.",
    features: [
      {
        icon: FaPlug,
        title: "API Design",
        text: "REST or GraphQL contracts designed around real clients and future maintainability.",
      },
      {
        icon: FaCloud,
        title: "Gateway Setup",
        text: "Gateway configuration, routing, service boundaries, and environment setup.",
      },
      {
        icon: FaExchangeAlt,
        title: "Traffic Control",
        text: "Rate limits, versioning, retries, error handling, and service-to-service routing.",
      },
      {
        icon: FaShieldAlt,
        title: "Security Layer",
        text: "JWT, OAuth, API keys, policies, secrets, and access rules handled carefully.",
      },
    ],
    snapshot: [
      { label: "Primary goal", value: "Make data and services communicate safely." },
      { label: "What we need", value: "Existing API docs, auth rules, target systems, and traffic expectations." },
      { label: "Common deliverable", value: "A documented gateway or integration layer with monitoring hooks." },
    ],
    outcomes: [
      "Cleaner communication between services and clients.",
      "A documented API surface your team can maintain.",
      "More predictable security, errors, and rate limiting.",
      "A stronger base for mobile apps, web apps, and automation.",
    ],
    included: [
      "API architecture review and contract planning.",
      "Gateway configuration or custom integration layer.",
      "Authentication, rate limit, and policy setup.",
      "Documentation and deployment support.",
    ],
    processIntro:
      "We start by understanding every system involved, then define a stable interface before implementation.",
    process: [
      { title: "Audit Systems", text: "Review services, clients, auth, data formats, and current pain points." },
      { title: "Design Contracts", text: "Define endpoints, payloads, versioning, security, and error behavior." },
      { title: "Implement Gateway", text: "Build or configure routing, policies, transformations, and integrations." },
      { title: "Monitor and Handoff", text: "Add logging, test flows, document usage, and prepare the team." },
    ],
    related: ["Web Applications", "Mobile Apps", "AI Agents"],
  },

  consultation: {
    eyebrow: "Consultation",
    title: "Get technical clarity before you commit time and budget.",
    description:
      "We help founders and teams validate ideas, choose architecture, plan MVPs, review systems, and make better build decisions.",
    cta: "Schedule Consultation",
    color: "#D9534F",
    icon: FaCommentsDollar,
    timeline: "Typical scope: 1-4 weeks",
    bestFor: "Teams that need direction before or during a build",
    featuresIntro:
      "The right consultation should leave you with decisions, tradeoffs, and next steps, not vague advice.",
    features: [
      {
        icon: FaBrain,
        title: "Idea Validation",
        text: "Clarify feasibility, user value, risks, and the smallest useful version.",
      },
      {
        icon: FaChartLine,
        title: "Tech Strategy",
        text: "Choose stacks, hosting, architecture, and integration plans that fit the business.",
      },
      {
        icon: FaUserTie,
        title: "Startup Advisory",
        text: "Shape MVP scope, team needs, launch plan, and practical product milestones.",
      },
      {
        icon: FaCogs,
        title: "System Review",
        text: "Review existing products for performance, maintainability, security, and scale issues.",
      },
    ],
    snapshot: [
      { label: "Primary goal", value: "Turn uncertainty into a practical plan." },
      { label: "What we need", value: "Current goals, constraints, product notes, and any existing system details." },
      { label: "Common deliverable", value: "Roadmap, architecture notes, and prioritized next steps." },
    ],
    outcomes: [
      "A clearer MVP or improvement roadmap.",
      "Known risks and tradeoffs before build decisions harden.",
      "Better stack, cost, and timeline expectations.",
      "Actionable next steps for design, development, or hiring.",
    ],
    included: [
      "Discovery and context review.",
      "Technical recommendation and tradeoff discussion.",
      "Roadmap or architecture notes.",
      "Follow-up support for next-step decisions.",
    ],
    processIntro:
      "We keep consultation concrete: understand the context, identify choices, and leave you with a usable path.",
    process: [
      { title: "Discover", text: "Understand the product, business goals, constraints, and current blockers." },
      { title: "Diagnose", text: "Identify technical options, risks, dependencies, and hidden complexity." },
      { title: "Plan", text: "Prioritize the next moves and shape a roadmap or architecture direction." },
      { title: "Support", text: "Review questions, refine decisions, and help prepare for execution." },
    ],
    related: ["Build Websites", "Web Applications", "AI Agents"],
  },

  adCreatives: {
    eyebrow: "Ad Creatives",
    title: "Create campaign assets people can understand in a scroll.",
    description:
      "We design ad creatives, banners, videos, and launch kits that make your offer clear across paid and organic channels.",
    cta: "Request Creatives",
    color: "#F0AD4E",
    icon: FaBullhorn,
    timeline: "Typical scope: 1-4 weeks",
    bestFor: "Brands running campaigns, product launches, or performance tests",
    featuresIntro:
      "Strong creative is built around message clarity, audience fit, channel requirements, and enough variants to learn from.",
    features: [
      {
        icon: FaPenNib,
        title: "Static Ads",
        text: "Display banners, social posts, launch graphics, and platform-ready dimensions.",
      },
      {
        icon: FaVideo,
        title: "Video Concepts",
        text: "Short-form concepts, reels, explainers, and hooks shaped for attention spans.",
      },
      {
        icon: FaPalette,
        title: "Creative System",
        text: "Reusable colors, type, layouts, and patterns to keep campaigns consistent.",
      },
      {
        icon: FaBullhorn,
        title: "Campaign Kits",
        text: "Ad sets, landing visuals, thumbnails, and supporting launch assets.",
      },
    ],
    snapshot: [
      { label: "Primary goal", value: "Help your message land quickly and consistently." },
      { label: "What we need", value: "Offer, audience, brand assets, channels, references, and campaign goal." },
      { label: "Common deliverable", value: "A set of ready-to-use campaign creatives and variants." },
    ],
    outcomes: [
      "Clearer campaign visuals with stronger message hierarchy.",
      "Multiple variants for testing hooks, formats, and offers.",
      "Channel-ready assets sized for the platforms you use.",
      "A more consistent brand presence across campaign touchpoints.",
    ],
    included: [
      "Creative brief and direction.",
      "Static or motion asset production.",
      "Review rounds and final export formats.",
      "Variant suggestions for campaign testing.",
    ],
    processIntro:
      "We keep the creative cycle tight so you can launch, learn, and improve without dragging production out.",
    process: [
      { title: "Brief", text: "Clarify audience, channel, offer, message, and creative references." },
      { title: "Concept", text: "Develop directions, hooks, layouts, and early visual drafts." },
      { title: "Refine", text: "Apply feedback, prepare variants, and polish the strongest options." },
      { title: "Deliver", text: "Export final files in the formats your campaign needs." },
    ],
    related: ["Build Websites", "Consultation Services", "CRM Platforms"],
  },

  webScrapers: {
    eyebrow: "Web Scrapers",
    title: "Collect web data reliably without repetitive manual work.",
    description:
      "We build custom scraping and data pipelines, backed by our in-house scraper components, that extract, clean, schedule, and deliver information your team can actually use.",
    cta: "Plan Data Extraction",
    color: "#5BC0DE",
    icon: FaSearch,
    timeline: "Typical scope: 2-8 weeks",
    bestFor: "Teams that need market, listing, product, or operational data",
    featuresIntro:
      "Useful scraping needs more than grabbing HTML. It needs source analysis, stable extraction, storage, monitoring, and ethical constraints.",
    features: [
      {
        icon: FaRobot,
        title: "Custom Bots",
        text: "Scrapers tailored to source structure, pagination, login flows, and data fields.",
      },
      {
        icon: FaTools,
        title: "In-House Scrapers",
        text: "Reusable internal scraper modules help us start faster for common listing, catalog, lead, and monitoring workflows.",
      },
      {
        icon: FaDatabase,
        title: "Clean Storage",
        text: "CSV, Excel, database, or API outputs shaped for downstream use.",
      },
      {
        icon: FaCloudDownloadAlt,
        title: "Scheduled Pipelines",
        text: "Cloud-ready jobs, retries, alerts, and recurring collection flows.",
      },
      {
        icon: FaChartLine,
        title: "Data Readiness",
        text: "Deduplication, normalization, validation, and summaries where useful.",
      },
    ],
    snapshot: [
      { label: "Primary goal", value: "Turn target websites into usable structured data." },
      { label: "What we need", value: "Source URLs, fields needed, frequency, output format, and usage context." },
      { label: "Common deliverable", value: "A scraper or pipeline with clean output and run instructions." },
    ],
    outcomes: [
      "Less manual collection and copy-paste work.",
      "Faster setup for common use cases through our in-house scraper foundation.",
      "Cleaner, repeatable datasets for business decisions.",
      "Scheduled extraction with easier monitoring.",
      "A data flow that can connect to dashboards or CRMs.",
    ],
    included: [
      "Source and feasibility review.",
      "Use of in-house scraper components where they fit the target source.",
      "Scraper development and output formatting.",
      "Data cleanup rules and duplicate handling.",
      "Deployment, run instructions, and monitoring notes.",
    ],
    processIntro:
      "We validate source behavior first, then build a pipeline that handles the realities of changing pages and messy data.",
    process: [
      { title: "Assess Sources", text: "Review target sites, access rules, fields, and technical feasibility." },
      { title: "Build Extractor", text: "Create reliable extraction, navigation, parsing, and error handling." },
      { title: "Shape Data", text: "Clean, normalize, deduplicate, and store data in the required format." },
      { title: "Schedule and Monitor", text: "Deploy recurring runs and document maintenance needs." },
    ],
    related: ["CRM Platforms", "Web Applications", "AI Agents"],
  },

  crmPlatforms: {
    eyebrow: "CRM Platforms",
    title: "Build a CRM that matches how your team actually sells and serves.",
    description:
      "We create custom CRM systems for leads, customers, pipelines, follow-ups, reporting, and operational workflows.",
    cta: "Design My CRM",
    color: "#9B59B6",
    icon: FaUsers,
    timeline: "Typical scope: 6-14 weeks",
    bestFor: "Sales, support, and operations teams with custom workflows",
    featuresIntro:
      "A useful CRM should reduce friction for the team, not become another place where work disappears.",
    features: [
      {
        icon: FaUsers,
        title: "Customer Records",
        text: "Contacts, companies, notes, activity history, documents, and relationship context.",
      },
      {
        icon: FaChartLine,
        title: "Pipeline Insights",
        text: "Lead stages, deal value, activity, conversion reporting, and team dashboards.",
      },
      {
        icon: FaEnvelopeOpenText,
        title: "Follow-Up Flows",
        text: "Reminders, automated emails, task queues, and communication timelines.",
      },
      {
        icon: FaCogs,
        title: "Custom Workflows",
        text: "Business-specific statuses, permissions, actions, integrations, and approvals.",
      },
    ],
    snapshot: [
      { label: "Primary goal", value: "Make customer and sales work easier to track and act on." },
      { label: "What we need", value: "Pipeline stages, user roles, existing spreadsheets/tools, and reports needed." },
      { label: "Common deliverable", value: "A custom CRM with user roles, workflows, and dashboards." },
    ],
    outcomes: [
      "A single source of truth for leads and customers.",
      "Fewer missed follow-ups and handoff gaps.",
      "Dashboards that show pipeline health and team activity.",
      "A system that fits your workflow instead of forcing one.",
    ],
    included: [
      "Workflow and data model planning.",
      "CRM screens, roles, and permissions.",
      "Reporting dashboards and activity tracking.",
      "Import, integration, and deployment support.",
    ],
    processIntro:
      "We design the CRM around your real pipeline first, then build screens and automation around that flow.",
    process: [
      { title: "Map Workflow", text: "Document lead stages, customer lifecycle, roles, and current pain points." },
      { title: "Design Data", text: "Define records, relationships, permissions, views, and dashboard needs." },
      { title: "Build CRM", text: "Implement modules, automations, reports, and integrations." },
      { title: "Train and Improve", text: "Support rollout, collect feedback, and tune workflows after use." },
    ],
    related: ["Web Applications", "API Gateways", "Ad Creatives"],
  },

  desktopApps: {
    eyebrow: "Desktop Apps",
    title: "Create desktop tools for focused work, offline use, and richer local control.",
    description:
      "We build desktop applications for Windows, macOS, and Linux with practical offline behavior, integrations, and update paths.",
    cta: "Plan Desktop App",
    color: "#17A2B8",
    icon: FaDesktop,
    timeline: "Typical scope: 6-16 weeks",
    bestFor: "Internal tools, offline workflows, device utilities, and power-user apps",
    featuresIntro:
      "Desktop apps are useful when a browser is not enough: local files, offline mode, device access, or deeper workflows.",
    features: [
      {
        icon: FaDesktop,
        title: "Cross-Platform Builds",
        text: "Apps prepared for Windows, macOS, Linux, or the specific platform you need.",
      },
      {
        icon: FaDatabase,
        title: "Local Data",
        text: "Offline storage, sync strategy, file handling, and data safety considerations.",
      },
      {
        icon: FaTools,
        title: "Advanced Workflows",
        text: "Power-user interactions, device integrations, background jobs, and automation.",
      },
      {
        icon: FaSyncAlt,
        title: "Updates and Support",
        text: "Packaging, release channels, update planning, and maintenance workflows.",
      },
    ],
    snapshot: [
      { label: "Primary goal", value: "Give users a dependable tool outside the browser." },
      { label: "What we need", value: "Target OS, data needs, offline rules, integrations, and distribution plan." },
      { label: "Common deliverable", value: "A packaged desktop app with installation and update guidance." },
    ],
    outcomes: [
      "A focused app experience for local or offline workflows.",
      "Better control over files, devices, and system integrations.",
      "A release plan for distributing updates to users.",
      "A maintainable base for future desktop features.",
    ],
    included: [
      "Platform and packaging plan.",
      "Desktop UI and workflow implementation.",
      "Local storage, sync, or integration setup.",
      "Installer, release, and maintenance guidance.",
    ],
    processIntro:
      "We shape the desktop app around the operating environments it needs to survive in, not just the screens it shows.",
    process: [
      { title: "Define Environment", text: "Clarify target OS, hardware, permissions, offline needs, and user context." },
      { title: "Design Workflows", text: "Plan screens, local data, background tasks, and integration points." },
      { title: "Build and Package", text: "Develop the app, test across target systems, and prepare installers." },
      { title: "Release and Maintain", text: "Document updates, support, monitoring, and future improvements." },
    ],
    related: ["API Gateways", "Web Applications", "AI Agents"],
  },

  aiAgents: {
    eyebrow: "AI Agents",
    title: "Design agentic AI systems with MCP, RAG, and reliable guardrails.",
    description:
      "We build task-focused AI agents for support, operations, research, reporting, and workflow automation, integrated with your tools, data, APIs, and MCP servers.",
    cta: "Build an Agent",
    color: "#E83E8C",
    icon: FaRobot,
    timeline: "Typical scope: 4-12 weeks",
    bestFor: "Teams automating support, research, operations, and tool-connected decisions",
    featuresIntro:
      "Good AI automation starts with bounded tasks, clean knowledge, controlled tools, clear handoffs, and measurable outcomes.",
    features: [
      {
        icon: FaSitemap,
        title: "Agentic Workflows",
        text: "Agents that plan steps, use tools, check context, and keep humans in control of exceptions.",
      },
      {
        icon: FaBrain,
        title: "RAG + Knowledge",
        text: "Retrieval, summarization, decision support, and internal knowledge assistance grounded in your data.",
      },
      {
        icon: FaServer,
        title: "MCP Servers",
        text: "Controlled tool servers that let agents safely reach CRMs, documents, APIs, databases, and business actions.",
      },
      {
        icon: FaShieldAlt,
        title: "Evaluation + Guardrails",
        text: "Test sets, access rules, review points, logging, and escalation paths for production-ready behavior.",
      },
    ],
    snapshot: [
      { label: "Primary goal", value: "Automate bounded tasks while preserving reliability, visibility, and oversight." },
      { label: "What we need", value: "Target workflows, data sources, examples, approval rules, APIs, and tool access." },
      { label: "Common deliverable", value: "A tested AI agent workflow with RAG, MCP tools, monitoring, and human handoff paths." },
    ],
    architecture: {
      eyebrow: "Agentic AI Architecture",
      title: "A detailed view of the AI system we can build",
      text:
        "The goal is not just a chatbot. It is a connected AI layer that can understand intent, retrieve context, call safe tools, generate useful work, and leave a traceable path for review.",
      layers: [
        {
          icon: FaBrain,
          title: "Gen AI Core",
          text: "Large language models for reasoning, drafting, classification, extraction, summarization, and decision support.",
        },
        {
          icon: FaDatabase,
          title: "RAG Context Layer",
          text: "Embeddings, vector search, document retrieval, database context, and freshness rules so answers stay grounded.",
        },
        {
          icon: FaServer,
          title: "MCP Server Layer",
          text: "Model Context Protocol servers that expose tools, files, records, APIs, and business actions with controlled permissions.",
        },
        {
          icon: FaTools,
          title: "Tool Calling",
          text: "Structured function calls for CRM updates, ticket triage, report creation, web scraping, notifications, and backend workflows.",
        },
        {
          icon: FaSitemap,
          title: "Agent Orchestration",
          text: "Planner, executor, reviewer, and handoff patterns for multi-step work that needs memory and state.",
        },
        {
          icon: FaShieldAlt,
          title: "Safety + Review",
          text: "Human approvals, audit logs, sensitive-data limits, fallbacks, evaluation examples, and monitoring dashboards.",
        },
      ],
      flow: ["Intent", "Retrieve", "Plan", "Call tools", "Generate", "Review", "Act"],
      useCases: [
        "Support agents that retrieve policy, summarize tickets, draft replies, and escalate risky cases.",
        "Sales or CRM agents that research leads, score opportunities, prepare follow-ups, and update records.",
        "Operations agents that inspect reports, classify work, trigger workflows, and keep audit trails.",
        "Research agents that scrape, structure, compare, and summarize market or competitor data.",
      ],
    },
    outcomes: [
      "Less repetitive manual work for your team.",
      "Faster responses, summaries, triage, or task completion.",
      "Tool-connected workflows instead of isolated chat experiences.",
      "RAG-backed answers that use your business context instead of generic guesses.",
      "Clear guardrails for data access, approvals, escalation, and auditability.",
    ],
    included: [
      "Workflow discovery and agent scope definition.",
      "Prompting, RAG, MCP server, tool, or API integration setup.",
      "Evaluation examples, reliability checks, and failure-mode reviews.",
      "Deployment, monitoring, and handoff documentation.",
    ],
    processIntro:
      "We keep agents grounded in measurable workflows, then test them with real examples, tool permissions, and review loops before expanding scope.",
    process: [
      { title: "Pick the Workflow", text: "Identify tasks, success criteria, edge cases, data sources, and approval needs." },
      { title: "Design the Agent", text: "Define prompts, RAG, tools, MCP servers, memory, handoffs, and safety boundaries." },
      { title: "Test and Integrate", text: "Run examples, tune behavior, connect tools, evaluate outputs, and verify actions." },
      { title: "Deploy and Monitor", text: "Launch with logging, review loops, and a plan for continuous improvement." },
    ],
    related: ["API Gateways", "CRM Platforms", "Web Scrapers"],
  },
};
